import React, { useEffect, useState } from 'react';
import { X, Plus, AlertTriangle, Check } from 'lucide-react';

// Sui ve Arc swap'larını ELLE ekle — tx digest'i / tx hash'i ile.
// (Arc'ta backend ayrı modül: arc_swap.py; sonuç formatı aynı olduğu için modal ortak.)
//
// Solana tarafı ağı tarayıp swap'ları kendiliğinden yakalıyor; Sui'de kullanıcı
// yalnız belirli işlemleri ekliyor. O yüzden burada tarama yok, digest var.
//
// Üç özel durum kullanıcıya sorulur, sessizce karar VERİLMEZ:
//   • tx başka bir cüzdana ait  → "yanlış cüzdan, emin misin"
//   • 3+ token değişmiş         → hangi ikisinin swap olduğunu seç
//   • swap değil (LP/transfer)  → sebebiyle birlikte bildir

interface LegPick { coinType: string; symbol: string; amount: number; }
interface Result {
  digest: string; status: string; error?: string; saved?: boolean;
  isLpTx?: boolean; sender?: string; legs?: LegPick[];
  swap?: { in_symbol: string; out_symbol: string; in_raw: string; out_raw: string;
           in_dec: number; out_dec: number };
}

interface Props {
  dark?: boolean;
  wallet: string;
  chain?: 'sui' | 'arc';
  sendWs: (m: any) => void;
  subscribe: (fn: (m: any) => void) => (() => void) | undefined;
  onClose: () => void;
  onAdded: () => void;
}

export default function SuiSwapAddModal({ dark = true, wallet, chain = 'sui', sendWs, subscribe, onClose, onAdded }: Props) {
  const isArc = chain === 'arc';
  const [text, setText]     = useState('');
  const [busy, setBusy]     = useState(false);
  const [res, setRes]       = useState<Result[]>([]);
  const [picks, setPicks]   = useState<Record<string, string[]>>({});

  const card  = dark ? 'bg-[#0a1628] border-[#164a9e]' : 'bg-white border-[#d4e0f0]';
  const sub   = dark ? 'bg-[#061530] border-[#0a2560]' : 'bg-[#eef3fa] border-[#d4e0f0]';
  const muted = dark ? 'text-blue-300/70' : 'text-slate-500';
  const inp   = dark ? 'bg-[#03112b] border-[#0a2560] text-blue-50'
                     : 'bg-white border-[#d4e0f0] text-slate-800';

  useEffect(() => {
    const unsub = subscribe((m: any) => {
      if (m.type !== 'sui_swaps_added') return;
      setBusy(false);
      setRes(m.payload?.results || []);
      if ((m.payload?.results || []).some((r: Result) => r.saved)) onAdded();
    });
    return () => unsub?.();
  }, [subscribe, onAdded]);

  const digests = () => text.split(/[\s,]+/).map(x => x.trim()).filter(Boolean);

  const send = (force = false) => {
    const ds = digests();
    if (!ds.length) return;
    setBusy(true);
    sendWs({ type: isArc ? 'add_arc_swaps' : 'add_sui_swaps', payload: { digests: ds, wallet, force, picks } });
  };

  const needsConfirm = res.some(r => r.status === 'wrong_wallet');
  const amount = (raw: string, dec: number) =>
    (Number(raw) / 10 ** dec).toLocaleString('en-US', { maximumFractionDigits: 6 });

  const badge = (r: Result) => {
    if (r.saved) return <span className="text-emerald-400 flex items-center gap-1"><Check size={11} /> eklendi</span>;
    if (r.status === 'wrong_wallet') return <span className="text-amber-400">yanlış cüzdan</span>;
    if (r.status === 'pick') return <span className="text-sky-300">token seç</span>;
    if (r.status === 'not_swap') return <span className={muted}>swap değil</span>;
    return <span className="text-rose-400">hata</span>;
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className={`w-[620px] max-w-full rounded-xl border ${card} p-4 shadow-2xl max-h-[85vh] overflow-y-auto`}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <Plus size={15} className="text-sky-400" />
          <span className="font-semibold text-sm">{isArc ? 'Arc swap ekle — tx hash' : 'Sui swap ekle — tx digest'}</span>
          <button onClick={onClose} className={`ml-auto ${muted} hover:text-rose-400`}><X size={16} /></button>
        </div>

        <p className={`text-[11px] mb-2 ${muted}`}>
          Cüzdan <span className="font-mono">{wallet.slice(0, 10)}…{wallet.slice(-4)}</span> ·
          birden çok {isArc ? 'hash' : 'digest'}'i alt alta ya da virgülle yazabilirsin.
          {isArc ? ' Gaz (USDC) swap miktarına karışmaz, ayrı maliyet olarak saklanır.'
                 : ' Gaz ücreti fiyattan arındırılır, ayrı maliyet olarak saklanır.'}
        </p>

        <textarea value={text} onChange={e => setText(e.target.value)} rows={4}
          placeholder={isArc ? '0x58baed9707…\n0xd178e97773…' : 'EEEV841y6bMm...\nATyH11EwBCGa...'}
          className={`w-full px-2 py-1.5 rounded-lg border font-mono text-[11px] ${inp}`} />

        {res.length > 0 && (
          <div className={`mt-3 rounded-lg border ${sub} p-2.5 text-[11px] space-y-2`}>
            {res.map(r => (
              <div key={r.digest} className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono opacity-70">{r.digest.slice(0, 14)}…</span>
                  {badge(r)}
                  {r.swap && (
                    <span className="ml-auto tabular-nums">
                      <span className="text-rose-400">−{amount(r.swap.in_raw, r.swap.in_dec)} {r.swap.in_symbol}</span>
                      <span className={muted}> → </span>
                      <span className="text-emerald-400">+{amount(r.swap.out_raw, r.swap.out_dec)} {r.swap.out_symbol}</span>
                    </span>
                  )}
                </div>
                {r.isLpTx && (
                  <p className="text-amber-400 flex items-start gap-1.5">
                    <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                    LP işlemi olarak işaretlendi — etkisi zaten pozisyona yazılı, gruba eklenemez.
                  </p>
                )}
                {r.status === 'wrong_wallet' && (
                  <p className={muted}>
                    gönderen <span className="font-mono">{(r.sender || '—').slice(0, 12)}…</span>
                    {r.error ? ` · ${r.error}` : ''}
                  </p>
                )}
                {r.status === 'not_swap' && <p className={muted}>{r.error}</p>}
                {r.status === 'error' && <p className="text-rose-400 break-all">{r.error}</p>}
                {r.status === 'pick' && r.legs && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={muted}>hangi ikisi:</span>
                    {r.legs.map(l => {
                      const sel = (picks[r.digest] || []).includes(l.coinType);
                      return (
                        <button key={l.coinType}
                          onClick={() => setPicks(p => {
                            const cur = p[r.digest] || [];
                            const next = sel ? cur.filter(x => x !== l.coinType)
                                             : [...cur, l.coinType].slice(-2);
                            return { ...p, [r.digest]: next };
                          })}
                          className={`px-1.5 py-0.5 rounded border tabular-nums ${sel
                            ? 'border-sky-500/60 bg-sky-500/15 text-sky-300'
                            : `${sub} ${muted}`}`}>
                          {l.amount >= 0 ? '+' : ''}{l.amount.toLocaleString('en-US', { maximumFractionDigits: 4 })} {l.symbol}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 mt-3">
          <button onClick={() => send(false)} disabled={busy || !digests().length}
            className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold bg-sky-600/25 border border-sky-500/50 text-sky-300 hover:bg-sky-600/40 disabled:opacity-40">
            {busy ? 'Okunuyor…' : res.length ? 'Tekrar dene' : 'Ekle'}
          </button>
          {needsConfirm && (
            <button onClick={() => send(true)} disabled={busy}
              className="px-3 py-2 rounded-lg text-xs font-semibold bg-amber-600/25 border border-amber-500/50 text-amber-200 hover:bg-amber-600/40">
              Yine de ekle
            </button>
          )}
          <button onClick={onClose} className={`px-3 py-2 rounded-lg text-xs border ${sub} ${muted}`}>Kapat</button>
        </div>
      </div>
    </div>
  );
}
